/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Ek alan API değerleri ile tarayıcı form değerleri arasındaki kayıpsız codec.
 */

export interface EntityCustomFieldCodecDefinition {
  code: string;
  label?: string;
  dataType?: string;
}

export interface EntityCustomFieldCodecValueRow {
  customField?: EntityCustomFieldCodecDefinition;
  valueText?: unknown;
  valueNumber?: unknown;
  valueDate?: unknown;
  valueBoolean?: unknown;
  valueJson?: unknown;
}

function padDatePart(value: number): string {
  return String(value).padStart(2, '0');
}

export function toEntityCustomFieldDateTimeLocal(value: unknown): string {
  const raw = value === null || value === undefined ? '' : String(value).trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw)) return raw;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  return [
    `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`,
    `${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`,
  ].join('T');
}

export function normalizeEntityCustomFieldSubmitValue(
  field: EntityCustomFieldCodecDefinition,
  value: unknown,
): unknown {
  if (value === undefined || value === '') return null;
  const type = String(field.dataType || 'text').trim().toLowerCase();
  if (type === 'number' && value !== null) return String(value).trim();
  if (type === 'datetime' && value !== null) {
    const date = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(date.getTime())) {
      throw new Error(`${field.label || field.code} için geçerli tarih ve saat zorunludur`);
    }
    return date.toISOString();
  }
  if (type === 'json' && typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch {
      throw new Error(`${field.label || field.code} geçerli JSON olmalıdır`);
    }
  }
  return value;
}

export function normalizeEntityCustomFieldValuesForSubmit(
  definitions: readonly EntityCustomFieldCodecDefinition[],
  values: Readonly<Record<string, unknown>> | null | undefined,
): Record<string, unknown> {
  const source = values ?? {};
  return Object.fromEntries(definitions.flatMap((field) => (
    Object.prototype.hasOwnProperty.call(source, field.code)
      ? [[field.code, normalizeEntityCustomFieldSubmitValue(field, source[field.code])]]
      : []
  )));
}

export function toEntityCustomFieldFormValue(
  field: EntityCustomFieldCodecDefinition,
  value: unknown,
): unknown {
  const type = String(field.dataType || 'text').toLowerCase();
  if (value === null || value === undefined) return type === 'boolean' ? false : '';
  if (type === 'number') return String(value);
  if (type === 'boolean') return Boolean(value);
  if (type === 'json') return JSON.stringify(value, null, 2);
  if (type === 'date') return String(value).slice(0, 10);
  if (type === 'datetime') return toEntityCustomFieldDateTimeLocal(value);
  return value;
}

export function readEntityCustomFieldValue(row: EntityCustomFieldCodecValueRow): unknown {
  const type = String(row.customField?.dataType || 'text').toLowerCase();
  const value = type === 'number'
    ? row.valueNumber
    : type === 'boolean'
      ? row.valueBoolean
      : type === 'json'
        ? row.valueJson
        : type === 'date' || type === 'datetime'
          ? row.valueDate
          : row.valueText;
  return toEntityCustomFieldFormValue(row.customField ?? { code: '', dataType: type }, value);
}
